import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Badge,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import moment from "moment/moment";
import {
  getWhatsappLinksAPI,
  createWhatsappLinkAPI,
  updateWhatsappLinkAPI,
  deleteWhatsappLinkAPI,
} from "../../service/WhatsappLinkService";
import "./WhatsappLinks.css";

const PHONE_PATTERN = /^\+?\d[\d\s\-]{6,18}\d$/;
const DEFAULT_COUNTRY_CODE = "91";

const phoneToWaLink = (phone) => {
  const trimmed = phone.trim();
  const hasCountryCode = trimmed.startsWith("+");
  const digits = trimmed.replace(/[\s\-+]/g, "");
  const withCode = hasCountryCode ? digits : `${DEFAULT_COUNTRY_CODE}${digits}`;
  return `https://wa.me/${withCode}`;
};

const waLinkToPhone = (link) => {
  if (!link) return "";
  const match = link.match(/wa\.me\/(\d+)/);
  return match ? `+${match[1]}` : link;
};

const formatDate = (link) => {
  const date = link.updatedAt || link.updated_at || link.createdAt || link.created_at;
  if (!date) return "—";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "—";
};

export default function WhatsappLinks() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = React.useRef();

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [deletingLink, setDeletingLink] = useState(null);

  const [formLink, setFormLink] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    const res = await getWhatsappLinksAPI();
    if (res?.code === 200) {
      setLinks(res.response || []);
    } else {
      toast.error("Failed to fetch WhatsApp links");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const validatePhone = (phone) => {
    if (!phone.trim()) return "Phone number is required";
    if (!PHONE_PATTERN.test(phone.trim()))
      return "Enter a valid phone number with country code (e.g. +91 98765 43210)";
    return "";
  };

  const handleOpenAdd = () => {
    setEditingLink(null);
    setFormLink("");
    setFormLabel("");
    setFormActive(true);
    setFormError("");
    onOpen();
  };

  const handleOpenEdit = (link) => {
    setEditingLink(link);
    setFormLink(waLinkToPhone(link.link));
    setFormLabel(link.label || "");
    setFormActive(link.isActive ?? true);
    setFormError("");
    onOpen();
  };

  const handleSubmit = async () => {
    const error = validatePhone(formLink);
    if (error) {
      setFormError(error);
      return;
    }
    setSubmitting(true);

    const payload = {
      link: phoneToWaLink(formLink.trim()),
      label: formLabel.trim() || null,
      isActive: formActive,
    };

    let res;
    if (editingLink) {
      res = await updateWhatsappLinkAPI(editingLink.id, payload);
    } else {
      res = await createWhatsappLinkAPI(payload);
    }

    if (res?.code === 200) {
      toast.success(
        editingLink ? "Link updated successfully" : "Link created successfully"
      );
      onClose();
      fetchLinks();
    } else {
      toast.error(res?.message || "Something went wrong");
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (link) => {
    const res = await updateWhatsappLinkAPI(link.id, {
      ...link,
      isActive: !link.isActive,
    });
    if (res?.code === 200) {
      toast.success(
        `Link ${!link.isActive ? "activated" : "deactivated"} successfully`
      );
      fetchLinks();
    } else {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingLink) return;
    const res = await deleteWhatsappLinkAPI(deletingLink.id);
    if (res?.code === 200) {
      toast.success("Link deleted successfully");
      onDeleteClose();
      setDeletingLink(null);
      fetchLinks();
    } else {
      toast.error("Failed to delete link");
    }
  };

  const handleOpenDelete = (link) => {
    setDeletingLink(link);
    onDeleteOpen();
  };

  return (
    <div className="main whatsapp-links-page">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 main-container">
            <div className="listing-grid">
              <div className="detail-row">
                <h2>WhatsApp Links</h2>
              </div>
              <Flex mt="25px">
                <Button colorScheme="green" onClick={handleOpenAdd}>
                  <i
                    className="fab fa-whatsapp"
                    style={{ marginRight: "8px" }}
                  />
                  Add WhatsApp Link
                </Button>
              </Flex>

              <div style={{ marginTop: "25px" }}>
                {loading ? (
                  <Text textAlign="center" py={8}>Loading...</Text>
                ) : links.length === 0 ? (
                  <Text textAlign="center" py={8}>No WhatsApp links found. Add one to get started.</Text>
                ) : isMobile ? (
                  /* Mobile: Card layout */
                  <Flex direction="column" gap={3}>
                    {links.map((link, index) => (
                      <Box
                        key={link.id}
                        border="1px solid #e2e8f0"
                        borderRadius="md"
                        p={4}
                        bg="white"
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="600" fontSize="sm">
                            {link.label || `Link #${index + 1}`}
                          </Text>
                          <Badge colorScheme={link.isActive ? "green" : "red"} fontSize="xs">
                            {link.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Flex>
                        <Text fontSize="sm" color="#333" mb={2} wordBreak="break-all">
                          <a href={link.link} target="_blank" rel="noopener noreferrer" style={{ color: "#000" }}>
                            {link.link}
                          </a>
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={3}>
                          {formatDate(link)}
                        </Text>
                        <Flex justify="space-between" align="center">
                          <Flex align="center" gap={2}>
                            <Switch
                              size="sm"
                              isChecked={link.isActive}
                              colorScheme="green"
                              onChange={() => handleToggleActive(link)}
                              aria-label={`Toggle active state for ${link.label || link.link}`}
                            />
                            <Text fontSize="xs" color="gray.500">
                              {link.isActive ? "Active" : "Inactive"}
                            </Text>
                          </Flex>
                          <Flex gap={2}>
                            <Button size="sm" colorScheme="blue" onClick={() => handleOpenEdit(link)}>
                              Edit
                            </Button>
                            <Button size="sm" colorScheme="red" onClick={() => handleOpenDelete(link)}>
                              Delete
                            </Button>
                          </Flex>
                        </Flex>
                      </Box>
                    ))}
                  </Flex>
                ) : (
                  /* Desktop: Table layout */
                  <div className="table-responsive data-table">
                    <div className="dataTables_wrapper no-footer">
                      <table
                        className="table table-bordered datatable dataTable no-footer"
                        role="grid"
                      >
                        <thead>
                          <tr role="row" style={{ backgroundColor: "#C9C9C9" }}>
                            <th style={{ minWidth: "40px" }}>#</th>
                            <th style={{ minWidth: "100px" }}>Label</th>
                            <th style={{ minWidth: "120px" }}>Link</th>
                            <th style={{ minWidth: "80px" }}>Status</th>
                            <th style={{ minWidth: "140px" }}>Updated At</th>
                            <th style={{ minWidth: "120px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {links.map((link, index) => (
                            <tr key={link.id}>
                              <td>{index + 1}</td>
                              <td>{link.label || "—"}</td>
                              <td className="link-cell" title={link.link}>
                                <a
                                  href={link.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#000" }}
                                >
                                  {link.link}
                                </a>
                              </td>
                              <td>
                                <Flex align="center" gap={2}>
                                  <Switch
                                    isChecked={link.isActive}
                                    colorScheme="green"
                                    onChange={() => handleToggleActive(link)}
                                    aria-label={`Toggle active state for ${link.label || link.link}`}
                                  />
                                  <Text
                                    fontSize="xs"
                                    color={link.isActive ? "green.600" : "red.500"}
                                    whiteSpace="nowrap"
                                  >
                                    {link.isActive ? "Active" : "Inactive"}
                                  </Text>
                                </Flex>
                              </td>
                              <td>{formatDate(link)}</td>
                              <td>
                                <div className="action-buttons">
                                  <Button
                                    size="sm"
                                    colorScheme="blue"
                                    onClick={() => handleOpenEdit(link)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="red"
                                    onClick={() => handleOpenDelete(link)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent mx={{ base: 4, md: "auto" }}>
          <ModalHeader>
            {editingLink ? "Edit" : "Add"} WhatsApp Link
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                placeholder="+91 98765 43210"
                value={formLink}
                onChange={(e) => {
                  setFormLink(e.target.value);
                  setFormError("");
                }}
                type="tel"
                maxLength={20}
              />
              {formError && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {formError}
                </Text>
              )}
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Label (optional)</FormLabel>
              <Input
                placeholder="e.g. Support, Sales"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                maxLength={100}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Active</FormLabel>
              <Switch
                isChecked={formActive}
                colorScheme="green"
                onChange={(e) => setFormActive(e.target.checked)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {editingLink ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={{ base: 4, md: "auto" }}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete WhatsApp Link
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this link
              {deletingLink?.label ? ` "${deletingLink.label}"` : ""}? This
              action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </div>
  );
}
